import { Circle, MagnifyingGlass, Plus, User } from "@phosphor-icons/react";
import React, { useEffect, useRef, useState } from "react";
import Select, { components } from "react-select";
import { Avatar, Badge, HideComponentWrapper, Typography } from "../../components";
import { ISelectInput } from "../../models";
import { IDropdownProps } from "../../models/IDropdownProps";
import styles from "./PopupSelect.module.css";
import { translatePhrase } from "../../utils/functions";

const customStyles = {
	groupHeading: (provided: any, state: any) => {
		return {
			...provided,
			fontSize: "100%",
			fontWeight: "600"
		};
	},
	control: (provided: any, state: any) => {
		return {
			...provided,
			"&:hover span": {
				background: "var(--text-primary)"
			},
			"&:hover div": {
				color: "var(--text-primary)"
			}
		};
	},
	placeholder: (provided: any, state: { children: string }) => {
		const fontFamily = state.children === "Sort By" ? "medium" : "unset";
		return {
			...provided,
			color: "var(--text-secondary)",
			fontFamily
		};
	},
	dropdownIndicator: (provided: any) => ({
		...provided,
		color: "var(--text-secondary)"
	}),
	indicatorSeparator: (provided: any) => ({
		...provided,
		backgroundColor: "var(--text-secondary)"
	}),
	option: (provided: { backgroundColor: any }, state: { isSelected: any; data: { className: string }; isFocused: any }) => ({
		...provided,
		"&:hover": {
			backgroundColor: state.isSelected ? provided.backgroundColor : "var(--bg-smoke-light)"
		},
		color: state.isSelected ? "#fff" : state.data.className === "group_title" ? "var(--text_muted)" : "unset",
		textTransform: state.data.className === "group_title" ? "uppercase" : "",
		fontFamily: state.data.className === "group_title" ? "medium" : "",
		backgroundColor: state.isSelected ? "#3490cf" : state.isFocused ? "var(--bg-smoke-light)" : "unset"
	}),
	menu: (provided: any, state: any) => ({
		...provided,
		borderRadius: "10px",
		boxShadow: "1px 9px 1.3125rem rgb(53 143 207 / 10%)"
	})
};

const Dropdown = (props: IDropdownProps) => {
	const selectLabelRef = useRef<any>(null);
	const [error, setError] = useState(props.errorMessage);
	const [isFieldInvalid, setIsFieldInvalid] = useState(false);
	useEffect(() => {
		const _isFieldInvalid = props.required && props.formSubmitted && (props.isMulti ? !props.value?.length : !props.value?.value);
		setIsFieldInvalid(!!_isFieldInvalid);
	}, []);
	const onClickLabel = () => {
		if (selectLabelRef.current) {
			selectLabelRef.current.focus();
		}
	};

	const handleChange = (event: ISelectInput) => {
		props.onChange(event, props.name ?? "");
		if (event?.value && isFieldInvalid) {
			setIsFieldInvalid(false);
		}
	};
	const defaultOption = ({ data, innerProps }: { data: any; innerProps: any }) => {
		return (
			<div
				{...innerProps}
				aria-disabled={data.disabled}
				className={`${styles.custom_option} ${props.value?.value === data.value ? styles.active : ""}`}
			>
				<div className={styles.default_dropdown_item}>
					{props.type === "icon-leading" && <User size="1.25rem" className={` ${props.iconClass}`} />}
					{props.type === "dot-leading" && <Circle weight="fill" size={10} className={`${styles.icon_dot} ${props.iconClass}`} />}
					{props.type === "avatar-leading" && (
						<Avatar
							size="sm"
							src={data.src}
							src_text={data.src_text}
							id={data.value}
							image_hash={data.image_hash}
							mime_type={data.mime_type}
							alt={data.alt || data.label}
							apiRoutePrefix={data.apiRoutePrefix || `${window.location.origin}/api/v1/Auth/get_file`}
						/>
					)}
					<Typography size="md" fontFamily="regular" type="text" color="primary">
						{data.label}
					</Typography>
					<HideComponentWrapper show={!!data.supportingText}>
						<Typography size="sm" fontFamily="regular" type="text" color="tertiary">
							{data.supportingText}
						</Typography>
					</HideComponentWrapper>
					<HideComponentWrapper show={!!data.badge}>
						<Badge size={"sm"} type={"pill-color"} icon={null} color={"grey"}>
							{data.badge}
						</Badge>
					</HideComponentWrapper>
				</div>
				<i className={`ph ph-check ${styles.activecheck}`}></i>
			</div>
		);
	};
	const CustomInput = (props: any) => (
		<div className={styles.inputWrapper}>
			<MagnifyingGlass size={"1.25rem"} className="icon-color" />
			<components.Input {...props} className={styles.input} />
		</div>
	);
	const DefaultInput = (props: any) => <components.Input {...props} className={styles.input} />;

	const getCustomComponents = () => {
		let item: any = {
			IndicatorSeparator: () => null,
			Input: props.hideSearchIcon ? DefaultInput : CustomInput,
			Option: defaultOption
		};
		if (props.CustomOption) {
			item = {
				...item,
				Option: props.CustomOption
			};
		}

		if (props.CustomMenuList) {
			item = {
				...item,
				MenuList: props.CustomMenuList
			};
		} else if (props.addNewSelectOption) {
			item = {
				...item,
				MenuList: CustomMenuListB
			};
		}
		return item;
	};
	const CustomMenuListB = (menuProps: any) => {
		return (
			<components.MenuList {...menuProps}>
				<div
					onClick={() => {
						menuProps.selectProps.onAddNewOption();
					}}
					className={styles.menulist_add_new_option}
				>
					<Plus size={16} weight="bold" className={styles.plus_icon} />
					<Typography size="md" fontFamily="medium" type="text" color="primary">
						{props.addNewOptionLabel ?? transformPhrase("addNew")}
					</Typography>
				</div>
				{menuProps.children}
			</components.MenuList>
		);
	};
	const transformPhrase = (phrase: string) => {
		if (props.transformPhrase && typeof props.transformPhrase === "function" && props.UiLanguage) {
			return props.transformPhrase(phrase, props.UiLanguage, {});
		} else {
			return translatePhrase(phrase) || phrase;
		}
	};
	const getMessageText = () => {
		if (props.value && props.required && error === transformPhrase("fieldRequired")) {
			setError("");
		}
		if (error) {
			return <span className={`${styles.error_text}`}>{error}</span>;
		} else if (props.formSubmitted && !isFieldInvalid && props.required) {
			setError(transformPhrase("fieldRequired"));
			return <span className={`${styles.error_text}`}>{transformPhrase("fieldRequired")}</span>;
		} else if (props.hintText) {
			return <span className={`${styles.hint_text}`}>{props.hintText}</span>;
		} else if (props.errorMessage) {
			return <span className={`${styles.error_text}`}>{props.errorMessage}</span>;
		}
	};
	const handleValidate = (e: React.FocusEvent<HTMLInputElement>) => {
		if (!props.required && !props.value) {
			setIsFieldInvalid(false);
			setError("");
			return true;
		}
		if (props.disableValidation) {
			return true;
		}
		if (!props.value && props.required) {
			setIsFieldInvalid(true);
			setError(transformPhrase("fieldRequired"));
		} else {
			setIsFieldInvalid(false);
			setError("");
		}
	};
	return (
		<div style={props.formStyle} className={`${styles.form_group} ${props.formClass ?? ""}`}>
			<HideComponentWrapper show={!!props.label}>
				<label
					onClick={onClickLabel}
					className={`${styles.form_label} ${props.required === true ? styles.required : styles.optional}`}
					htmlFor={props.id}
				>
					{props.label}
				</label>
			</HideComponentWrapper>
			<div
				className={`${styles.input_cont} ${props.tooltip ? "dropdown_has_tooltip" : ""} ${
					props.hideSearchIcon ? "" : "dropdown_custom_input"
				}`}
			>
				<HideComponentWrapper show={!!props.tooltip}>
					<div className={styles.help_icon_cont}>
						<div className={styles.help_icon}>
							<i className={`ph-bold ph-question ${styles.form_field_info_icon}`}></i>
						</div>
						<div className={styles.tooltip_popup}>{props.tooltip && <span>{props.tooltip}</span>}</div>
					</div>
				</HideComponentWrapper>
				<Select
					options={props.options}
					onChange={handleChange}
					onBlur={handleValidate}
					value={props.value}
					isClearable={props.isClearable}
					id={props.id}
					className={`react-select ${styles.form_field} ${styles.select_input_field} ${props.className ?? ""} ${
						isFieldInvalid ? "field_invalid" : ""
					}`}
					classNamePrefix={`${props.classNamePrefix ?? ""} ${props.filterClass ?? "select_dropdown"}`}
					placeholder={props.placeholder}
					ref={selectLabelRef}
					isDisabled={props.disabled}
					name={props.name}
					isMulti={props.isMulti}
					isLoading={props.isLoading}
					style={props.style}
					filterOption={props.filterOption}
					styles={customStyles}
					components={getCustomComponents()}
					menuIsOpen={props.menuIsOpen}
					onAddNewOption={props.handleAddNewOption}
					// menuPortalTarget={document.body}
				/>
				{getMessageText()}
			</div>
		</div>
	);
};

export default Dropdown;
